/**
 * Minimax + alpha-beta search for Rithmomachia.
 *
 * Branching factor is high (~100–200 actions per ply at midgame) so we lean on:
 *  - Iterative deepening: search depth 1, 2, 3… until the time budget is spent.
 *  - Move ordering: captures first, then moves into enemy territory, then the rest.
 *  - Alpha-beta pruning.
 *  - Quiescence-style extension: when the immediate play would capture, we always extend
 *    one ply at the leaves (no horizon effect on tactical sequences).
 *
 * The bot evaluates positions with a weighted sum of:
 *  - Material on board
 *  - Trophy difference (captures already taken)
 *  - Pieces in enemy territory (worth more if they're part of partial progressions)
 *  - Mobility (legal-move count)
 *  - Pyramid survival
 */

import {
  allLegalMoves,
  applyMove,
  applyTurn,
  classifyProgression,
  combinations,
  isInEnemyTerritory,
  isInGreaterVictoryArea,
  type Capture,
  type GameState,
  type Move,
  type Piece,
  type Side,
} from "../engine";

export interface BotAction {
  move: Move;
  declared?: Capture;
  advance?: boolean;
}

export interface SearchOptions {
  /** Hard wall-clock deadline in ms (performance.now()). The search returns the deepest fully completed PV before this. */
  deadlineMs?: number;
  /** Maximum depth to search to. Default 5. */
  maxDepth?: number;
  /** If false, skip iterative deepening and search exactly at maxDepth. */
  iterativeDeepening?: boolean;
}

const INF = 1e9;

/** Top-level entrypoint: pick the best action for `side` to play in `state`. */
export function chooseBestAction(state: GameState, side: Side, opts: SearchOptions = {}): BotAction | null {
  if (state.toMove !== side) return null;
  const deadline = opts.deadlineMs ?? performance.now() + 1500;
  const maxDepth = opts.maxDepth ?? 5;

  const rootActions = generateActions(state);
  if (rootActions.length === 0) return null;
  orderActions(rootActions);

  let best: BotAction = pickAction(rootActions[0]);

  if (opts.iterativeDeepening === false) {
    const result = rootSearch(state, side, rootActions, maxDepth, deadline);
    return result ? result.action : best;
  }

  // Iterative deepening: keep deeper searches' result if completed in time.
  for (let depth = 1; depth <= maxDepth; depth++) {
    if (performance.now() >= deadline) break;
    const result = rootSearch(state, side, rootActions, depth, deadline);
    if (!result) break;
    best = result.action;
    // Move ordering for next depth: place the best-known PV first.
    rootActions.sort((a, b) =>
      sameAction(a, best) ? -1 : sameAction(b, best) ? 1 : 0,
    );
    // Early exit on detected forced win/loss
    if (Math.abs(result.score) > 100000) break;
  }

  return best;
}

/**
 * Compare a played move against what the engine considers best.
 *
 * Used by the tutorial review system. Returns:
 *   - bestAction: the engine's preferred action from the same root position
 *   - bestScore: minimax score of the best action
 *   - playedScore: minimax score of the played action
 *   - delta: bestScore - playedScore (always ≥ 0; how much worse the played move is)
 *   - classification: a human-friendly label based on the delta
 *
 * Returns null if `playedAction` isn't a legal action from `state` for `side`.
 */
export interface MoveReview {
  bestAction: BotAction;
  bestScore: number;
  playedScore: number;
  delta: number;
  classification: "best" | "great" | "good" | "inaccuracy" | "mistake" | "blunder";
  /** True if the played action is byte-for-byte the engine's best. */
  wasBest: boolean;
}

export function reviewMove(
  state: GameState,
  side: Side,
  playedAction: BotAction,
  opts: SearchOptions = {},
): MoveReview | null {
  if (state.toMove !== side) return null;
  const deadline = opts.deadlineMs ?? performance.now() + 1500;
  const maxDepth = opts.maxDepth ?? 3;

  const rootActions = generateActions(state);
  if (rootActions.length === 0) return null;
  orderActions(rootActions);

  // Score every root action at the requested depth. We don't use rootSearch directly because
  // we need the score for the SPECIFIC played action, not just the best. So we score them all.
  const scored: Array<{ action: BotAction; wrapped: ActionWithApplied; score: number }> = [];
  let bestScore = -INF;
  let bestWrapped: ActionWithApplied | null = null;

  for (const wrapped of rootActions) {
    if (performance.now() >= deadline) break;
    const score = -negamax(wrapped.next, opposite(side), maxDepth - 1, -INF, INF, deadline);
    scored.push({ action: pickAction(wrapped), wrapped, score });
    if (score > bestScore) {
      bestScore = score;
      bestWrapped = wrapped;
    }
  }
  if (!bestWrapped) return null;

  // Find the score for the played action. If we can't match it (shouldn't happen for legal
  // play), fall back to bestScore so delta is 0.
  const played = scored.find((s) => sameAction(s.action, playedAction));
  const playedScore = played ? played.score : bestScore;
  const wasBest = sameAction(pickAction(bestWrapped), playedAction);
  const delta = Math.max(0, bestScore - playedScore);
  const classification = classifyDelta(wasBest ? 0 : delta);

  return {
    bestAction: pickAction(bestWrapped),
    bestScore,
    playedScore,
    delta,
    classification,
    wasBest,
  };
}

/**
 * Map an eval delta to a human-friendly label. Thresholds are tuned for Rithmomachia's
 * value scale (a single Round-9 is ~9 raw material points; a Square-100 is ~100). One full
 * Round captured ≈ 7-9 in delta units; a King layer ≈ 25-64.
 */
function classifyDelta(delta: number): MoveReview["classification"] {
  if (delta <= 1) return "best";
  if (delta <= 5) return "great";
  if (delta <= 15) return "good";
  if (delta <= 35) return "inaccuracy";
  if (delta <= 80) return "mistake";
  return "blunder";
}

interface RootResult {
  action: BotAction;
  score: number;
}

function rootSearch(_state: GameState, side: Side, rootActions: ActionWithApplied[], depth: number, deadline: number): RootResult | null {
  let alpha = -INF;
  const beta = INF;
  let bestAction: BotAction | null = null;
  let bestScore = -INF;

  for (const wrapped of rootActions) {
    if (performance.now() >= deadline) {
      // Time's up — abort this depth iteration; iterative deepening will keep the previous depth's PV.
      if (bestAction) return { action: bestAction, score: bestScore };
      return null;
    }
    const child = wrapped.next;
    const score = -negamax(child, opposite(side), depth - 1, -beta, -alpha, deadline);
    if (score > bestScore) {
      bestScore = score;
      bestAction = pickAction(wrapped);
    }
    if (score > alpha) alpha = score;
  }
  if (!bestAction) return null;
  return { action: bestAction, score: bestScore };
}

/**
 * Negamax: a clean variant of minimax that flips the score for the opponent.
 * Returns evaluation from the to-move side's perspective.
 */
function negamax(state: GameState, side: Side, depth: number, alpha: number, beta: number, deadline: number): number {
  if (state.victory) {
    // `side` here is the side to MOVE at this node. Victory is set by the previous move, so
    // victory.side === opposite(side) in normal flow — the side to move just lost.
    // The first branch is defensive (e.g. resignation paths set on this side's behalf); it
    // shouldn't be reached by ordinary minimax descent, but the bookkeeping is correct if it is.
    if (state.victory.side === side) return 1_000_000;
    return -1_000_000;
  }
  if (depth <= 0 || performance.now() >= deadline) {
    return evaluate(state, side);
  }

  const actions = generateActions(state);
  if (actions.length === 0) {
    // Stalemate — already encoded as victory in the engine, but defensive default.
    return -1_000_000;
  }
  orderActions(actions);

  let value = -INF;
  for (const w of actions) {
    const child = w.next;
    const score = -negamax(child, opposite(side), depth - 1, -beta, -alpha, deadline);
    if (score > value) value = score;
    if (value > alpha) alpha = value;
    if (alpha >= beta) break; // alpha-beta cutoff
  }
  return value;
}

function pickAction(w: ActionWithApplied): BotAction {
  return { move: w.move, declared: w.declared, advance: w.advance };
}

function sameAction(a: BotAction, b: BotAction): boolean {
  if (a.move.from.file !== b.move.from.file) return false;
  if (a.move.from.rank !== b.move.from.rank) return false;
  if (a.move.to.file !== b.move.to.file) return false;
  if (a.move.to.rank !== b.move.to.rank) return false;
  if ((a.move.mode ?? null) !== (b.move.mode ?? null)) return false;
  if ((a.declared?.target.file ?? -1) !== (b.declared?.target.file ?? -1)) return false;
  if ((a.declared?.target.rank ?? -1) !== (b.declared?.target.rank ?? -1)) return false;
  if ((a.advance ?? false) !== (b.advance ?? false)) return false;
  return true;
}

function opposite(side: Side): Side {
  return side === "white" ? "black" : "white";
}

interface ActionWithApplied {
  move: Move;
  declared?: Capture;
  advance?: boolean;
  next: GameState;
  capturedValue: number; // for ordering
  intoEnemyTerritory: boolean;
}

/** Generate every (move, optional declared capture, advance) triple, with the resulting state. */
function generateActions(state: GameState): ActionWithApplied[] {
  const out: ActionWithApplied[] = [];
  const moves = allLegalMoves(state);
  for (const move of moves) {
    let pending;
    try {
      pending = applyMove(state, move);
    } catch {
      continue;
    }
    const piece = state.board.get(squareKey(move.from));
    const enteringEnemy = piece ? isInEnemyTerritory(piece.side, move.to.rank) : false;
    const siegeValue = pending.sieges.reduce((acc, c) => {
      const t = state.board.get(squareKey(c.target));
      return acc + (t?.value ?? 0);
    }, 0);

    // Action: skip declared capture
    {
      const result = applyTurn(state, move);
      out.push({
        move,
        next: result.state,
        capturedValue: siegeValue,
        intoEnemyTerritory: enteringEnemy,
      });
    }

    // Each Encounter/Assault/Deceit option
    for (const c of pending.declarable) {
      const target = pending.state.board.get(squareKey(c.target));
      const declaredValue = target?.value ?? 0;
      const advanceable = (c.method === "encounter" || c.method === "deceit") && c.attackers.length === 1;
      const advanceOptions: boolean[] = advanceable ? [false, true] : [false];
      for (const advance of advanceOptions) {
        try {
          const result = applyTurn(state, move, { declared: c, advance });
          out.push({
            move,
            declared: c,
            advance,
            next: result.state,
            capturedValue: siegeValue + declaredValue,
            intoEnemyTerritory: enteringEnemy,
          });
        } catch {
          // skip invalid (shouldn't happen)
        }
      }
    }
  }
  return out;
}

function orderActions(actions: ActionWithApplied[]): void {
  actions.sort((a, b) => {
    if (a.capturedValue !== b.capturedValue) return b.capturedValue - a.capturedValue;
    if (a.intoEnemyTerritory !== b.intoEnemyTerritory) return a.intoEnemyTerritory ? -1 : 1;
    return 0;
  });
}

function squareKey(sq: { file: number; rank: number }): `${number},${number}` {
  return `${sq.file},${sq.rank}`;
}

// =====================================================================
// Evaluation function
// =====================================================================

const W_MATERIAL = 1.0;
const W_TROPHY = 1.2;
const W_TERRITORY = 0.7;
const W_PROGRESSION_PARTIAL = 12.0;
const W_PROGRESSION_FULL = 25.0;
const W_PYRAMID_ALIVE = 60;
const W_MOBILITY = 0.04;

/** Static evaluation. Positive = good for `side`. */
export function evaluate(state: GameState, side: Side): number {
  const enemy = opposite(side);
  let score = 0;

  // Material
  let myMaterial = 0;
  let enemyMaterial = 0;
  let myPyramidAlive = false;
  let enemyPyramidAlive = false;
  // Half-board presence — generic "pushed past midline" signal for the territory heuristic.
  const myInEnemyTerritory: Piece[] = [];
  const enemyInOurTerritory: Piece[] = [];
  // V1 strict triumph zone (opponent's first five ranks) — pieces here are what actually
  // count toward Greater/Royal/Glorious progressions. Used for progressionProgress only.
  const myInTriumphZone: Piece[] = [];
  const enemyInTriumphZone: Piece[] = [];

  for (const [k, p] of state.board) {
    if (p.side === side) {
      myMaterial += p.value;
      if (p.shape === "pyramid") myPyramidAlive = true;
      const sq = parseKey(k);
      if (isInEnemyTerritory(side, sq.rank)) myInEnemyTerritory.push(p);
      if (isInGreaterVictoryArea(side, sq.rank)) myInTriumphZone.push(p);
    } else {
      enemyMaterial += p.value;
      if (p.shape === "pyramid") enemyPyramidAlive = true;
      const sq = parseKey(k);
      if (isInEnemyTerritory(enemy, sq.rank)) enemyInOurTerritory.push(p);
      if (isInGreaterVictoryArea(enemy, sq.rank)) enemyInTriumphZone.push(p);
    }
  }
  score += W_MATERIAL * (myMaterial - enemyMaterial);

  // Trophy difference (already-captured)
  const myTrophy = state.captured[side].reduce((a, p) => a + p.value, 0);
  const enemyTrophy = state.captured[enemy].reduce((a, p) => a + p.value, 0);
  score += W_TROPHY * (myTrophy - enemyTrophy);

  // Territory presence (broader half-board check — "advance forward" pressure)
  score += W_TERRITORY * (myInEnemyTerritory.length - enemyInOurTerritory.length);

  // Progression progress — strict V1 triumph zone only. A partial AP/GP/HP formed in the
  // mid-board doesn't actually trigger a Greater Victory, so don't reward it.
  score += W_PROGRESSION_PARTIAL * progressionProgress(myInTriumphZone, state.victoryConfig.level);
  score -= W_PROGRESSION_PARTIAL * progressionProgress(enemyInTriumphZone, state.victoryConfig.level);

  // Pyramid survival
  if (myPyramidAlive) score += W_PYRAMID_ALIVE;
  if (enemyPyramidAlive) score -= W_PYRAMID_ALIVE;

  // Mobility (cheap heuristic — count my pieces and approximate)
  score += W_MOBILITY * (myMaterial > 0 ? estimateMobility(state, side) : 0);
  score -= W_MOBILITY * (enemyMaterial > 0 ? estimateMobility(state, enemy) : 0);

  return score;
}

function parseKey(k: string): { file: number; rank: number } {
  const [f, r] = k.split(",").map(Number) as [number, number];
  return { file: f, rank: r };
}

/** Estimates mobility cheaply: sum of empty orthogonal neighbors per piece. */
function estimateMobility(state: GameState, side: Side): number {
  let count = 0;
  for (const [k, p] of state.board) {
    if (p.side !== side) continue;
    const sq = parseKey(k);
    for (const [df, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nf = sq.file + df;
      const nr = sq.rank + dr;
      if (nf < 0 || nf >= 8 || nr < 0 || nr >= 16) continue;
      if (!state.board.has(`${nf},${nr}`)) count++;
    }
  }
  return count;
}

/**
 * Returns a rough score for how close the given pieces (already filtered to enemy territory) are
 * to forming progressions of the relevant types.
 *
 * Strategy: count 3-element subsets that satisfy any progression type. Any complete progression
 * weighs full; partial progress is reflected in the count of pieces in enemy territory (already
 * scored in W_TERRITORY).
 */
function progressionProgress(pieces: Piece[], level: GameState["victoryConfig"]["level"]): number {
  if (pieces.length < 3) return 0;
  // For Common/Proper, progressions don't matter — return 0.
  if (level === "common" || level === "proper") return 0;

  let progressionCount = 0;
  const distinctTypes = new Set<string>();
  for (const triple of combinations(pieces, 3)) {
    const types = classifyProgression(triple.map((p) => p.value));
    progressionCount += types.length;
    for (const t of types) distinctTypes.add(t);
  }
  let score = progressionCount;
  if (level === "greater" && progressionCount >= 1) score += W_PROGRESSION_FULL;
  if (level === "royal" && distinctTypes.size >= 2) score += W_PROGRESSION_FULL;
  if (level === "glorious" && distinctTypes.size >= 3) score += W_PROGRESSION_FULL * 2;
  return score;
}
